

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewPaymentTransactionModal = ({
    isOpen,
    onClose,
    onSave,
    invoiceId = null
}) => {
    const { axiosInstance } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        travel_order_id: '',
        invoice_id: invoiceId || '',
        payment_date: new Date().toISOString().slice(0, 16),
        payment_method: 'Card',
        payment_status: 'Payment Pending',
        transaction_id: '',
        payment_type: 'Customer',
        amount: '',
        surcharge_amount: '',
        payment_mode: 'Full Payment',
        installment_number: '',
        payment_due_date: '',
        stripe_intent_id: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [invoices, setInvoices] = useState([]);
    const [travelOrders, setTravelOrders] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [loadingTravelOrders, setLoadingTravelOrders] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchInvoices();
            if (!invoiceId) {
                fetchTravelOrders();
            }
        }
    }, [isOpen, invoiceId]);

    const fetchTravelOrders = async () => {
        setLoadingTravelOrders(true);
        try {
            const response = await axiosInstance.get('/travel-orders/');
            setTravelOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching travel orders:', error);
        } finally {
            setLoadingTravelOrders(false);
        }
    };

    const fetchInvoices = async () => {
        setLoadingInvoices(true);
        try {
            const response = await axiosInstance.get('/invoices/');
            setInvoices(response.data || []);
        } catch (error) {
            console.error('Error fetching invoices:', error);
        } finally {
            setLoadingInvoices(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            if (name === 'travel_order_id') {
                next.invoice_id = '';
            }
            if (name === 'invoice_id' && value) {
                const selectedInvoice = invoices.find(inv => String(inv.id) === String(value));
                if (selectedInvoice?.travel_order_id) {
                    next.travel_order_id = selectedInvoice.travel_order_id;
                }
            }
            return next;
        });
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const filteredInvoices = formData.travel_order_id
        ? invoices.filter(inv => String(inv.travel_order_id) === String(formData.travel_order_id))
        : invoices;

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Transaction Name is required';
        if (!formData.invoice_id) errors.invoice_id = 'Invoice is required';
        if (!formData.payment_date) errors.payment_date = 'Payment Date is required';
        if (!formData.amount) errors.amount = 'Amount is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                invoice_id: parseInt(formData.invoice_id),
                travel_order_id: formData.travel_order_id ? parseInt(formData.travel_order_id) : null,
                payment_date: new Date(formData.payment_date).toISOString(),
                amount: parseFloat(formData.amount),
                surcharge_amount: formData.surcharge_amount ? parseFloat(formData.surcharge_amount) : null,
                installment_number: formData.installment_number ? parseInt(formData.installment_number) : null,
                payment_due_date: formData.payment_due_date || null,
            };

            await onSave(payload);
            onClose();
        } catch (error) {
            console.error('Error saving Payment Transaction:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Payment Transaction',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '900px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">New Payment Transaction</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                {!invoiceId && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal">
                                            <InputLabel>Order Id</InputLabel>
                                            <Select
                                                name="travel_order_id"
                                                value={formData.travel_order_id}
                                                onChange={handleInputChange}
                                                label="Order Id"

                                                disabled={loadingTravelOrders}
                                            >
                                                <MenuItem value=""><em>Select Travel Order</em></MenuItem>
                                                {travelOrders.map(order => (
                                                    <MenuItem key={order.id} value={order.id}>
                                                        {order.name || order.order_id || order.id}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}

                                {!invoiceId && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.invoice_id} required>
                                            <InputLabel>Invoice</InputLabel>
                                            <Select
                                                name="invoice_id"
                                                value={formData.invoice_id}
                                                onChange={handleInputChange}
                                                label="Invoice"

                                                disabled={loadingInvoices}
                                            >
                                                <MenuItem value=""><em>Select an Invoice</em></MenuItem>
                                                {filteredInvoices.map(inv => (
                                                    <MenuItem key={inv.id} value={inv.id}>{inv.invoice_number || inv.id}</MenuItem>
                                                ))}
                                            </Select>
                                            {formErrors.invoice_id && <MDTypography variant="caption" color="error">{formErrors.invoice_id}</MDTypography>}
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Payment Date"
                                        name="payment_date"
                                        type="datetime-local"
                                        value={formData.payment_date}
                                        onChange={handleInputChange}
                                        error={!!formErrors.payment_date}
                                        helperText={formErrors.payment_date}
                                        required
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Payment Method</InputLabel>
                                        <Select
                                            name="payment_method"
                                            value={formData.payment_method}
                                            onChange={handleInputChange}
                                            label="Payment Method"

                                        >
                                            <MenuItem value="Card">Card</MenuItem>
                                            <MenuItem value="BankTransfer">Bank Transfer</MenuItem>
                                            <MenuItem value="PayID">PayID</MenuItem>
                                            <MenuItem value="Cash">Cash</MenuItem>
                                            <MenuItem value="Other">Other</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Payment Status</InputLabel>
                                        <Select
                                            name="payment_status"
                                            value={formData.payment_status}
                                            onChange={handleInputChange}
                                            label="Payment Status"

                                        >
                                            <MenuItem value="Payment Pending">Payment Pending</MenuItem>
                                            <MenuItem value="Paid">Paid</MenuItem>
                                            <MenuItem value="Failed">Failed</MenuItem>
                                            <MenuItem value="Refunded">Refunded</MenuItem>
                                            <MenuItem value="PartiallyRefunded">Partially Refunded</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Payment Mode</InputLabel>
                                        <Select
                                            name="payment_mode"
                                            value={formData.payment_mode}
                                            onChange={handleInputChange}
                                            label="Payment Mode"

                                        >
                                            <MenuItem value="Deposit">Deposit</MenuItem>
                                            <MenuItem value="Final Balance">Final Balance</MenuItem>
                                            <MenuItem value="Installment">Installment</MenuItem>
                                            <MenuItem value="Full Payment">Full Payment</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Payment Type</InputLabel>
                                        <Select
                                            name="payment_type"
                                            value={formData.payment_type}
                                            onChange={handleInputChange}
                                            label="Payment Type"

                                        >
                                            <MenuItem value="Customer">Customer</MenuItem>
                                            <MenuItem value="Supplier">Supplier</MenuItem>
                                            <MenuItem value="Adjustment">Adjustment</MenuItem>
                                            <MenuItem value="Refund">Refund</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Amount"
                                        name="amount"
                                        type="number"
                                        value={formData.amount}
                                        onChange={handleInputChange}
                                        error={!!formErrors.amount}
                                        helperText={formErrors.amount}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Surcharge Amount"
                                        name="surcharge_amount"
                                        type="number"
                                        value={formData.surcharge_amount}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                {(formData.payment_mode === 'Installment') && (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Installment Number"
                                            name="installment_number"
                                            type="number"
                                            value={formData.installment_number}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            margin="normal"
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Payment Due Date"
                                        name="payment_due_date"
                                        type="date"
                                        value={formData.payment_due_date}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Transaction ID"
                                        name="transaction_id"
                                        value={formData.transaction_id}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Stripe Intent ID"
                                        name="stripe_intent_id"
                                        value={formData.stripe_intent_id}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Payment Transaction'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewPaymentTransactionModal;

